import { useEffect } from "react";
import { NumberParam, useQueryParam, withDefault } from "use-query-params";

import { PAGE_SIZE_OPTIONS } from "common/constants";

export default function usePagination(localStorageId: string) {
  const pageSizeKey = `table-${localStorageId}/pageSize`;

  // Get default page size from localStorage or fallback to PAGE_SIZE_OPTIONS
  const getStoredPageSize = () => {
    const storedPageSize = localStorage.getItem(pageSizeKey);

    if (storedPageSize) {
      const parsedPageSize = Number(storedPageSize);

      // Validate that the stored value is in PAGE_SIZE_OPTIONS
      if (PAGE_SIZE_OPTIONS.includes(parsedPageSize)) {
        return parsedPageSize;
      }
    }

    return PAGE_SIZE_OPTIONS[1];
  };

  const [pageParam, setPageParam] = useQueryParam(
    "page",
    withDefault(NumberParam, 1),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  const [pageSizeParam, setPageSizeParam] = useQueryParam(
    "pageSize",
    withDefault(NumberParam, getStoredPageSize()),
    {
      removeDefaultsFromUrl: true,
      updateType: "replaceIn",
    },
  );

  useEffect(() => {
    if (!PAGE_SIZE_OPTIONS.includes(pageSizeParam)) {
      setPageSizeParam(getStoredPageSize());
    }
  }, [pageSizeParam]);

  function setPage(newPage: number) {
    setPageParam(newPage + 1);
  }

  function setPageSize(newPageSize: number) {
    setPageSizeParam(newPageSize);
    // Store the new page size in localStorage
    localStorage.setItem(pageSizeKey, newPageSize.toString());
  }

  // the page is stored 1 based in the url, but returned 0 based
  const safePage = Math.max(0, pageParam - 1);

  // Clamp to a valid option synchronously so the component never renders with
  // an out-of-bounds value from the URL (e.g. ?pageSize=99999) before the
  // effect corrects it.
  const safePageSize = PAGE_SIZE_OPTIONS.includes(pageSizeParam)
    ? pageSizeParam
    : getStoredPageSize();

  return { page: safePage, setPage, pageSize: safePageSize, setPageSize };
}
